import React, { Fragment, useEffect } from 'react';
import cx from 'classnames';

import ProductDetailPanel from '../../ProductDetailPanel';
import InputForm from '../../../../../components/form/InputForm';
import SwitchForm from '../../../../../components/form/SwitchForm';
import BaseForm from '../../../../../components/form/BaseForm';
import CheckBox from '../../../../../components/input/CheckBox';
import DateRangePicker from '../../../../../components/datePicker/DateRangePicker';
import Select from '../../../../../components/input/Select';
import { setPriceDataFromDiscountType } from '../../../../../lib/utils/productUtils';
import { compareProductVersion } from '../../../../../lib/utils';
import { setNumberWithComma } from '../../../../../lib/common';
import { formRequiredRules } from '../../../../../lib/formRules';
import { REX_TEN_WON_UNIT } from '../../../../../lib/rex';
import {
  DISCOUNT_TYPE,
  DISCOUNT_TYPE_SELECT_LIST,
  MAXIMUM_NUMBER_OF_PRODUCT_NAME,
  PRODUCT_DETAIL_PAGE_PANEL_NAME,
  PRODUCT_MAXIMUM_DISCOUNT_RATE,
  PRODUCT_MAXIMUM_PRICE,
  PRODUCT_MINIMUM_DISCOUNT_AMOUNT,
  PRODUCT_MINIMUM_DISCOUNT_RATE,
} from '../../../../../constants/service/app/productConstants';
import { DATE_FORMAT } from '../../../../../constants/service/common/utilConstants';
import { REQUIRED_MSG } from '../../../../../constants/service/common/formRulesConstants';
import './ProductNameAndPrice.scss';

const ProductNameAndPrice = ({
  isEditable,
  form,
  initialValue,
  showInfo,
  isCompare,
  comparisonData,
  minimumPrice,
  ...res
}) => {
  const {
    originPrice,
    salesPrice,
    discountType,
    discountAmount,
    discountRate,
    discountPeriod,
    isDiscount,
    isDiscountPeriod,
  } = form.getFieldsValue();
  const isDiscountAmountType = discountType === DISCOUNT_TYPE.FIXED_AMOUNT_DISCOUNT;
  const isDiscountRateType = discountType === DISCOUNT_TYPE.FIXED_RATE_DISCOUNT;

  useEffect(() => {
    form.setFieldsValue({
      salesPrice: initialValue.salesPrice,
      discountType: initialValue.discountType,
      discountAmount: initialValue.discountAmount,
      discountRate: initialValue.discountRate,
      discountPeriod: initialValue.discountPeriod,
      isDiscount: initialValue.isDiscount,
      isDiscountPeriod: initialValue.isDiscountPeriod,
    });
  }, [initialValue]);

  useEffect(() => {
    form.setFieldsValue({
      discountData: isDiscountAmountType ? discountAmount : isDiscountRateType ? discountRate : null,
    });
  }, [isDiscountAmountType, isDiscountRateType, discountType, discountAmount, discountRate]);

  const getDiscountDataProps = () => {
    let rules = [formRequiredRules(isDiscount, REQUIRED_MSG, false)];
    let min, max;
    let step;

    if (isDiscountAmountType) {
      /**
       * 정액 할인
       */
      rules.push(
        { pattern: REX_TEN_WON_UNIT, message: '10원 단위로 입력해주세요.' },
        {
          validator: (rule, value, cb) => {
            if (value && originPrice) {
              /**
               *  실판매가 > 9900원
               */
              if (
                Number(form.getFieldValue('originPrice')) - Number(value.toString().replace(/[^\d]+/g, '')) <
                minimumPrice
              ) {
                cb(`판매가는 ${minimumPrice}원 이상이어야 합니다.`);
              }
              cb();
            }
            cb();
          },
        },
      );
      min = PRODUCT_MINIMUM_DISCOUNT_AMOUNT;
      step = 10;
    } else if (isDiscountRateType) {
      /**
       * 정률 할인
       */
      rules.push({
        validator: (rule, value, cb) => {
          if (value && originPrice) {
            /**
             * 실판매가 > 9900원
             */
            if (
              Math.floor((Number(form.getFieldValue('originPrice')) * (100 - value) * 0.01) / 10) * 10 <
              minimumPrice
            ) {
              cb(`판매가는 ${minimumPrice}원 이상이어야 합니다.`);
            }
            cb();
          }
          cb();
        },
      });
      min = PRODUCT_MINIMUM_DISCOUNT_RATE;
      max = PRODUCT_MAXIMUM_DISCOUNT_RATE;
      step = 1;
    }

    return {
      rules,
      min,
      max,
      step,
    };
  };

  const setPriceFieldsValue = (discountType, originPrice, amount, rate) => {
    const result = setPriceDataFromDiscountType(discountType, originPrice, amount, rate);
    form.setFieldsValue({
      ...result,
    });
  };

  const onChangeOriginPrice = (price) => {
    if (isDiscount) {
      setPriceFieldsValue(discountType, price, discountAmount, discountRate);
    } else {
      form.setFieldsValue({ salesPrice: price });
    }
  };

  const onChangeDiscountToggle = (isDiscount) => {
    if (isDiscount) {
      setPriceFieldsValue(discountType, originPrice);
    } else {
      form.setFieldsValue({ salesPrice: originPrice });
    }

    form.setFieldsValue({ isDiscount });
    form.validateFields();
  };

  const onChangeDiscountPrice = (discountData) => {
    setPriceFieldsValue(discountType, originPrice, discountData, discountData);
  };

  const onChangeDiscountType = (discountType) => {
    setPriceFieldsValue(discountType, originPrice);

    form.setFieldsValue({ discountType });
    form.setFieldsValue({ discountData: 10 });
  };

  const onChangeDiscountPeriodToggle = (isDiscountPeriod) => {
    form.setFieldsValue({ isDiscountPeriod });
  };

  const onSelectDiscountPeriod = (discountPeriod) => {
    form.setFieldsValue({ discountPeriod });
  };

  return (
    <ProductDetailPanel
      name={PRODUCT_DETAIL_PAGE_PANEL_NAME.PRO_NAME_AND_PRICE}
      className="product-name-and-price"
      title="상품명/판매가"
      isCompare={isCompare}
      {...res}
    >
      <div className="panel-body">
        {/* 상품명*/}
        <InputForm
          label="상품명"
          className={isCompare && compareProductVersion(initialValue.name, comparisonData.name)}
          form={form}
          rules={[formRequiredRules(true, REQUIRED_MSG)]}
          isEditable={isEditable}
          initialValue={initialValue.name}
          maxLength={MAXIMUM_NUMBER_OF_PRODUCT_NAME}
          name="name"
          showInfo={showInfo}
          subContent={
            <div className="sub-info">
              <div>
                *상품명의 글자 수는 최대 {MAXIMUM_NUMBER_OF_PRODUCT_NAME}
                자입니다.
              </div>
            </div>
          }
        />

        {/* 판매가 */}
        <InputForm
          label="판매가"
          type="number"
          className={isCompare && compareProductVersion(initialValue.originPrice, comparisonData.originPrice)}
          form={form}
          style={{ width: 200 }}
          rules={[
            formRequiredRules(true, REQUIRED_MSG, false),
            {
              pattern: REX_TEN_WON_UNIT,
              message: '10원 단위로 입력해주세요.',
            },
            {
              validator: (rule, value, cb) => {
                if (value < minimumPrice) cb(`판매가는 ${minimumPrice}원 이상이어야 합니다.`);
                else cb();
              },
            },
            {
              validator: (rule, value, cb) => {
                if (isDiscount && value) form.validateFields(['discountData'], { force: true });
                cb();
              },
            },
          ]}
          isEditable={isEditable}
          max={PRODUCT_MAXIMUM_PRICE}
          step={100}
          initialValue={initialValue.originPrice}
          onChange={onChangeOriginPrice}
          addonAfter="원"
          name="originPrice"
        />

        {/* 할인 여부 */}
        <SwitchForm label="할인" checked={isDiscount} disabled={!isEditable} onChange={onChangeDiscountToggle} />

        {/* 할인 설정인 경우에만 사용*/}
        {isDiscount ? (
          <Fragment>
            {/* 할인 설정 */}
            <InputForm
              label="할인 설정"
              type="number"
              className={
                isCompare &&
                compareProductVersion(
                  [initialValue.discountType, initialValue.discountPeriod],
                  [comparisonData.discountType, comparisonData.discountPeriod],
                )
                  ? 'different'
                  : ''
              }
              form={form}
              style={{ width: 200 }}
              {...getDiscountDataProps()}
              isEditable={isEditable}
              unit={DISCOUNT_TYPE_SELECT_LIST[discountType].name}
              onChange={onChangeDiscountPrice}
              addonAfter={
                <Select
                  list={DISCOUNT_TYPE_SELECT_LIST}
                  onChange={onChangeDiscountType}
                  defaultValue={discountType}
                  value={discountType}
                  isTable={true}
                />
              }
              name="discountData"
              subContent={
                <div className="sub-info">
                  <CheckBox
                    checked={isDiscountPeriod}
                    text="특정기간만 할인"
                    disabled={!isEditable}
                    onChange={onChangeDiscountPeriodToggle}
                  />
                  {isDiscountPeriod ? (
                    <DateRangePicker
                      value={discountPeriod}
                      isEditable={isEditable}
                      showTime={true}
                      startText="할인시작시각"
                      endText="할인종료시각"
                      outFormat={DATE_FORMAT.FULL_DASH_FORMAT}
                      btnTextFormat={DATE_FORMAT.FULL_DASH_FORMAT}
                      format={DATE_FORMAT.FULL_DASH_FORMAT}
                      onSelect={onSelectDiscountPeriod}
                    />
                  ) : null}
                </div>
              }
            />

            {/* 할인율 */}
            <BaseForm
              className={cx(
                'emphasize',
                isCompare && compareProductVersion(initialValue.discountRate, comparisonData.discountRate),
              )}
              label="할인율"
              content={setNumberWithComma(discountRate, '%')}
            />

            {/* 할인액*/}
            <BaseForm
              className={cx(
                'emphasize',
                isCompare && compareProductVersion(initialValue.discountAmount, comparisonData.discountAmount),
              )}
              label="할인액"
              content={setNumberWithComma(discountAmount, '원')}
            />
          </Fragment>
        ) : null}

        {/* 실판매가*/}
        <BaseForm
          className={cx(
            'emphasize',
            isCompare && compareProductVersion(initialValue.salesPrice, comparisonData.salesPrice),
          )}
          label="실판매가"
          content={setNumberWithComma(salesPrice, '원')}
        />
      </div>
      <div className="hidden-form">
        <InputForm form={form} type="hidden" name="salesPrice" />
        <InputForm form={form} type="hidden" name="discountType" />
        <InputForm form={form} type="hidden" name="discountAmount" />
        <InputForm form={form} type="hidden" name="discountRate" />
        <InputForm form={form} type="hidden" name="discountPeriod" />
        <InputForm form={form} type="hidden" name="isDiscount" />
        <InputForm form={form} type="hidden" name="isDiscountPeriod" />
      </div>
    </ProductDetailPanel>
  );
};

export default ProductNameAndPrice;
